// 과목의 강의보드 페이지 입니다.
import 'dayjs/locale/ko'

import { ChevronUpIcon } from '@heroicons/react/20/solid'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { useMediaQuery } from '@mantine/hooks'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOfAfter from 'dayjs/plugin/isSameOrAfter'
import { useParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import CurriculumKlassCard from '@/features/curriculum/components/CurriculumKlassCard'
import CurriculumLMSSection from '@/features/curriculum/components/CurriculumLMSSection'
import { useMyMergedProjectQuery } from '@/features/curriculum/project/queries/useMyProjectListQuery'
import { useProjectAttrsQuery } from '@/features/curriculum/project/queries/useProjectAttrsQuery'
import { useInstituteDashboardsQuery } from '@/features/klass/queries/useInstituteDashboardsQuery'
import WithCurriculumLayout from '@/shared/layout/WithCurriculumLayout'
import { WarningIcon } from '@/shared/layout/WithCurriculumLayout/Icons'
import { useAuth } from '@/shared/providers/Auth'
import { useCurriculum } from '@/shared/providers/Curriculum'

dayjs.extend(isBetween)
dayjs.locale('ko')
dayjs.extend(isSameOfAfter)

type ILmsMenuItem = {
  id: string
  isVisible: boolean
}

const CurriculumDetailPage = () => {
  const { curriculumId } = useParams() as { curriculumId: string }
  const { profile } = useAuth()
  const { curriculum, refetch } = useCurriculum()
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { data: dashboardData, isLoading: isDashboardDataLoading } = useInstituteDashboardsQuery(
    curriculumId,
    profile?.id as number,
  )
  const { data: projectAttrs } = useProjectAttrsQuery(curriculum?.instituteCode, curriculumId)
  const curriculumPeriod = useMemo(() => {
    const curDate = dayjs().startOf('day')
    if (curriculum) {
      const startDate = dayjs(curriculum.startedAt)
      const endDate = dayjs(curriculum.endedAt)

      if (curDate.isBefore(startDate)) {
        // 수강 시작일 전
        return '예정'
      } else if (curDate.isAfter(endDate)) {
        // 수강 종료일 후
        return '종료'
      } else {
        // 수강 중
        const remainingDays = endDate.diff(curDate, 'day')
        return remainingDays
      }
    }
  }, [curriculum])

  const { data: myMergedProjectData } = useMyMergedProjectQuery(
    projectAttrs,
    curriculum?.instituteCode,
    curriculumId,
  )

  useEffect(() => {
    refetch()
  }, [])

  const curDate = dayjs().format('YYYY-MM-DD')

  const isActive = useMemo(
    () =>
      curDate >= dateFormat(curriculum?.startedAt) && curDate <= dateFormat(curriculum?.endedAt),
    [curriculum],
  )

  const lmsMenu = ['project', 'survey', 'notice', 'qna'].reduce<Record<string, ILmsMenuItem>>(
    (acc, menu) => {
      acc[menu] = {
        id: menu,
        isVisible: curriculum?.lms.includes(menu) as boolean,
      }
      return acc
    },
    {},
  )

  const curProject = useMemo(() => {
    if (!myMergedProjectData || myMergedProjectData.length === 0) return null

    const notSubmittedProjects = myMergedProjectData.filter(
      project => !project.isSubmitted && dayjs(project.endedAt).isSameOrAfter(curDate),
    )

    if (notSubmittedProjects.length > 0) {
      return notSubmittedProjects.reduce((closest, project) => {
        const projectDate = dayjs(project.startedAt)
        const closestDate = dayjs(closest.startedAt)
        return Math.abs(projectDate.diff(curDate)) < Math.abs(closestDate.diff(curDate))
          ? project
          : closest
      })
    }

    return myMergedProjectData.reduce((closest, project) => {
      const projectDate = dayjs(project.startedAt)
      const closestDate = dayjs(closest.startedAt)
      return Math.abs(projectDate.diff(curDate)) < Math.abs(closestDate.diff(curDate))
        ? project
        : closest
    })
  }, [myMergedProjectData, curriculumId, curDate])

  const isCompleted = useMemo(() => {
    if (curriculum) return curriculum.averageRate >= curriculum.resourceCompleteRate
    return false
  }, [curriculum])

  return (
    <WithCurriculumLayout isLoading={isDashboardDataLoading}>
      <main className="w-full">
        <div className="flex flex-col gap-12 lg:gap-16">
          <div className="flex flex-col">
            {isMobile && (
              <p className="mb-2 self-start rounded bg-primary-subdued p-1 text-sm font-semibold text-primary-normal">
                {typeof curriculumPeriod === 'number' ? `D-${curriculumPeriod}` : curriculumPeriod}
              </p>
            )}
            <h3 className="text-xl font-semibold leading-normal text-label-normal md:text-2xl md:leading-snug lg:text-[28px]">
              {curriculum?.name}
            </h3>
            <p className="mt-1 flex flex-row gap-2 text-base font-medium text-label-neutral lg:mt-2 lg:text-lg">
              교육 기간{' '}
              <span>
                {dayjs(curriculum?.startedAt).format('YY.MM.DD')} ~{' '}
                {dayjs(curriculum?.endedAt).format('YY.MM.DD')}
              </span>
            </p>
            {isActive ? (
              <div className="mt-4 rounded-xl border bg-gray-100 p-3 lg:bg-white lg:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <CheckBadgeIcon
                      className={`h-auto w-5 ${isCompleted ? 'text-primary-normal' : 'text-neutral-500'}`}
                    />
                    <span className="text-base font-semibold leading-none text-neutral-500">
                      수료 조건 {`${isCompleted ? '달성' : '미달성'}`}
                    </span>
                  </div>
                  {isMobile ? (
                    <ChevronUpIcon
                      className={`${isOpen ? '' : 'rotate-180'} w-5 cursor-pointer text-gray-alternative`}
                      onClick={() => setIsOpen(!isOpen)}
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <InformationCircleIcon className="w-5 text-gray-alternative" />
                      <span className="text-sm font-medium text-gray-alternative">
                        전체 강의의 평균 진도율 {curriculum?.resourceCompleteRate}% 이상 달성시 수료
                      </span>
                    </div>
                  )}
                </div>
                {(isOpen || !isMobile) && (
                  <div className="mt-4 rounded-lg bg-white p-3 lg:p-0 lg:pt-5">
                    {isMobile && (
                      <div className="flex items-center gap-1">
                        <InformationCircleIcon className="w-5 text-gray-alternative" />
                        <span className="text-sm font-medium text-gray-alternative">
                          전체 강의의 평균 진도율 {curriculum?.resourceCompleteRate}% 이상 달성시
                          수료
                        </span>
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="relative h-4 w-full rounded bg-neutral-50">
                        <div
                          className="absolute h-4 rounded-l bg-neutral-200"
                          style={{ width: `${curriculum?.resourceCompleteRate}%` }}
                        ></div>
                        <div
                          className="absolute h-4 w-1/5 rounded-l bg-primary-normal"
                          style={{ width: `${curriculum?.averageRate}%` }}
                        ></div>
                        {!isMobile && (
                          <div className="absolute -top-7 left-0 text-sm font-semibold text-gray-alternative">
                            과정 수료율
                          </div>
                        )}
                      </div>
                      <p className="shrink-0 font-semibold text-label-alternative">
                        <span className="text-primary-normal">
                          {Math.floor(curriculum?.averageRate ?? 0)}%
                        </span>{' '}
                        | 100%
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 flex flex-row items-center justify-center gap-2.5 rounded-lg bg-[rgba(239,68,68,0.10)] py-2.5">
                <WarningIcon />
                <p className="text-sm font-semibold text-[#ff0000] lg:text-base">
                  지금은 교육기간이 아닙니다.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4 lg:gap-6">
            <h4 className="text-lg font-semibold text-label-normal lg:text-2xl">강의 목록</h4>
            {/* 강의 목록 강의카드 */}
            <div className="flex flex-col gap-4 md:flex-row md:flex-wrap lg:flex-col lg:flex-nowrap lg:gap-6">
              {curriculum?.courses?.map(({ courseId, imageM, title }) => (
                <CurriculumKlassCard
                  key={`curriculum_${courseId}`}
                  curriculumId={curriculumId}
                  courseId={courseId}
                  isActive={isActive}
                  imageUrl={imageM}
                  title={title}
                />
              ))}
            </div>
          </div>
          {/* 강의 활동 / 강의 자료실 */}
          {dashboardData && (
            <CurriculumLMSSection
              curriculumId={curriculumId}
              lmsMenu={lmsMenu}
              dashboardData={dashboardData}
              curProject={curProject}
            />
          )}
        </div>
      </main>
    </WithCurriculumLayout>
  )
}

export default CurriculumDetailPage

const dateFormat = (date: string | undefined) => dayjs(date).format('YYYY-MM-DD')
